/**
 * Client for the route API, plus the local memory of which routes we may edit.
 *
 * Since the gate arrived the edit token is a fallback rather than the main
 * credential: signing in authorises edits to everything. It is kept because a
 * browser that saved a route before the gate existed still holds one, and
 * because it costs nothing.
 */

import type { DraftRoute, RouteDoc } from '@cad/core';

const EDIT_TOKEN_PREFIX = 'cad.edit.';

export function rememberEditToken(routeId: string, token: string): void {
  try {
    localStorage.setItem(EDIT_TOKEN_PREFIX + routeId, token);
  } catch {
    // Private browsing, or storage full. The route still saved; it just can't be
    // edited again from this browser. Not worth failing the save over.
  }
}

export function recallEditToken(routeId: string): string | null {
  try {
    return localStorage.getItem(EDIT_TOKEN_PREFIX + routeId);
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function unwrap(response: Response): Promise<any> {
  if (response.ok) return response.status === 204 ? null : response.json();
  let message = `Request failed (${response.status})`;
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    // Non-JSON error body; the status-based message will do.
  }
  throw new ApiError(message, response.status);
}

export interface SavedRoute {
  id: string;
  revision: number;
  editToken?: string;
}

export interface RoutePayload extends DraftRoute {
  geometry: string;
  distance: number;
  duration: number;
  legs: RouteDoc['legs'];
  bbox: RouteDoc['bbox'];
  speedLimits?: RouteDoc['speedLimits'];
  maneuvers?: RouteDoc['maneuvers'];
  startName?: string;
  endName?: string;
}

export async function createRoute(payload: RoutePayload): Promise<SavedRoute> {
  const result = await unwrap(
    await fetch('/api/routes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  );
  rememberEditToken(result.id, result.editToken);
  return { id: result.id, revision: result.revision, editToken: result.editToken };
}

export async function updateRoute(id: string, payload: RoutePayload): Promise<SavedRoute> {
  const token = recallEditToken(id);
  const result = await unwrap(
    await fetch(`/api/routes/${id}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', ...(token ? { 'x-cad-edit-token': token } : {}) },
      body: JSON.stringify(payload),
    }),
  );
  return { id: result.id, revision: result.revision };
}

export async function fetchRoute(id: string): Promise<RouteDoc> {
  return unwrap(await fetch(`/api/routes/${id}`)) as Promise<RouteDoc>;
}

export async function deleteRoute(id: string): Promise<void> {
  const token = recallEditToken(id);
  await unwrap(
    await fetch(`/api/routes/${id}`, {
      method: 'DELETE',
      ...(token ? { headers: { 'x-cad-edit-token': token } } : {}),
    }),
  );
}

/** One row of the route list: enough to choose between them, and nothing more. */
export interface RouteSummary {
  id: string;
  name: string;
  notes?: string;
  distance: number;
  duration: number;
  stops: number;
  startName?: string;
  endName?: string;
  revision: number;
  createdAt: number;
  updatedAt: number;
}

export async function listRoutes(): Promise<RouteSummary[]> {
  const result = await unwrap(await fetch('/api/routes'));
  return result.routes as RouteSummary[];
}

export interface Session {
  signedIn: boolean;
  /** False when the server has no credentials set, so the UI can say why. */
  configured: boolean;
  username?: string;
}

export async function fetchSession(): Promise<Session> {
  return unwrap(await fetch('/api/session')) as Promise<Session>;
}

export async function signIn(username: string, password: string): Promise<Session> {
  return unwrap(
    await fetch('/api/session', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }),
  ) as Promise<Session>;
}

export async function signOut(): Promise<void> {
  await unwrap(await fetch('/api/session', { method: 'DELETE' }));
}
